import { makeAutoObservable } from 'mobx'
import { isAxiosError } from 'axios'
import { debounce } from 'lodash'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { RequestCancelAxios } from 'shared/api/lib'
import { HubspotWorkflowsApi } from 'entities/HubspotWorkflows/api/hubspotWorkflows'
import {
  type IParamsGetHubspotWorkflows,
  type IResponseGetHubspotWorkflows,
  type IResponseHubspotWorkflow,
} from 'entities/HubspotWorkflows/api/types'
import { HubspotWorkflow } from 'entities/HubspotWorkflows/model/HubspotWorkflow'
import { HubspotWorkflowRow } from 'entities/HubspotWorkflows/model/HubspotWorkflowRow'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'

export class HubspotWorkflowsListStore {
  private _isInitialLoading = true
  private _startSync = false
  private _search = ''
  private _itemsMap = new Map<number, HubspotWorkflow>()
  readonly tableStore = new TableStore<HubspotWorkflowRow>({
    key: 'hubspotWorkflows',
    limit: 10,
    page: 1,
    getLimit: () => tableLimitStore.getLimit('hubspot-workflows-table'),
    setLimit: (value) => tableLimitStore.setLimit('hubspot-workflows-table', value),
  })
  private _cancelRequestLoad = new RequestCancelAxios()
  private _cancelRequestRefresh = new RequestCancelAxios()
  private _hasRetriedAtMinLimit = false
  debounceLoadData: ReturnType<typeof debounce>

  constructor(private _entities: IEntitiesStore) {
    this.debounceLoadData = debounce(this.loadData, 500)

    makeAutoObservable(this, {
      debounceLoadData: false,
    })
  }

  get isInitialLoading() {
    return this._isInitialLoading
  }

  get search() {
    return this._search
  }

  get paramsGetItems(): IParamsGetHubspotWorkflows {
    return {
      search: this._search,
      length: this.tableStore.limit,
      page: this.tableStore.page,
    }
  }

  get items() {
    return Array.from(this._itemsMap.values()).map((workflow) => new HubspotWorkflowRow(workflow))
  }

  get isEmpty() {
    return !this.tableStore.loading && !this._itemsMap.size && !this._search
  }

  get startSync() {
    return this._startSync
  }

  init = () => {
    this.loadData()

    return this.dispose
  }

  reSync = () => {
    this._loadRefresh()
  }

  setItem = (response: IResponseHubspotWorkflow) => {
    const workflow = new HubspotWorkflow(response, this._entities)

    this._itemsMap.set(workflow.id, workflow)
  }

  setSearch = (search: string) => {
    this._search = search
    this.tableStore.setLoading(true)
    this.debounceLoadData()
  }

  setLoading = (value: boolean) => {
    this.tableStore.setLoading(value)
    this._isInitialLoading = false
  }

  private _loadRefresh = async () => {
    if (this._startSync) return

    try {
      this._cancelRequestRefresh.init()

      this._startSync = true

      await HubspotWorkflowsApi.getHubspotWorkflowRefresh({
        cancelToken: this._cancelRequestRefresh.token,
      })

      showToast({
        title: 'Update in progress',
        desc: 'We’re updating your workflow stats. We’ll notify you once it’s complete.',
        type: 'info',
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadData = async (noLoading?: boolean) => {
    let retrying = false

    try {
      this._cancelRequestLoad.init()

      if (!noLoading) {
        this.tableStore.setLoading(true)
      }
      const { data } = await HubspotWorkflowsApi.getHubspotWorkflows(this.paramsGetItems, {
        cancelToken: this._cancelRequestLoad.token,
      })
      this._setData(data)
      this._hasRetriedAtMinLimit = false
    } catch (e) {
      const isTimeout =
        isAxiosError(e) && (e.response?.status === 408 || e.response?.status === 504)
      if (isTimeout) {
        logger.warn(
          'TABLE: hubspot-workflows-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
        )
        const currentLimit = this.tableStore.limit
        const newLimit = tableLimitStore.reduceLimit('hubspot-workflows-table', currentLimit)
        if (newLimit !== currentLimit) {
          this._hasRetriedAtMinLimit = false
          this.tableStore.setLimit(newLimit)
          retrying = true
          this.loadData()
        } else if (!this._hasRetriedAtMinLimit) {
          this._hasRetriedAtMinLimit = true
          retrying = true
          this.loadData()
        }
      } else {
        logger.error(e)
      }
    } finally {
      if (!retrying) this.setLoading(false)
    }
  }

  private _setData = ({ data, meta }: IResponseGetHubspotWorkflows) => {
    this._itemsMap.clear()
    this.tableStore.setTotal(meta.total)

    data.forEach((item) => {
      this.setItem(item)
    })
  }

  setStartSync = (value: boolean) => {
    this._startSync = value
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this.tableStore.setPage(page)
    this.tableStore.setLimit(limit)
    this.loadData()
  }

  disposeParams = () => {
    this.tableStore.setTotal(0)
    this.tableStore.setPage(1)
    this.tableStore.setLimit(10)
    this._search = ''
  }

  dispose = () => {
    this.debounceLoadData.cancel()
    this.disposeParams()
    this._cancelRequestLoad.reset()
    this._cancelRequestRefresh.reset()
  }
}
